import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { SearchBox } from "../../../components/Search";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
// ▶ DEMO — import from DemoRestApi + demo const path
import {
  getRajasthanGssRestrictedAreaSummaryMetrics,
  getRajasthanGssRestrictedSummaryMetricsDiscom,
  getRajasthanGssLoadMetricsRestrictedDiscomDistrict,
} from "../../../Rest_api/DemoRestApi";
import { formatDateForAPI } from "../const";
import { getTimeRange } from "../../Dashboard/Exchange3/Chart";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip from "../../../components/Metrics";
import { getRestrictedLoadMetrics } from "../../Rajasthan/Utility/RestApi";
import { DiscomLoadMetrics } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { normalizeDisplayLabel } from "../../../utility/displayText";

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh", "LU"];

const GROUP_BY = [
  { label: "Area", key: "area", values: ["area", "discom"] }, // ▶ DEMO — "area" (prod: "station")
  { label: "Discom", key: "discom", values: ["discom"] },
  { label: "District", key: "district", values: ["district", "discom"] },
];

// ▶ DEMO — masked discom names
const DISCOM = [
  { label: "Discom 1", key: "Discom 1", apiKey: "Discom 1" },
  { label: "Discom 2", key: "Discom 2", apiKey: "Discom 2" },
  { label: "Discom 3", key: "Discom 3", apiKey: "Discom 3" },
];

function getApiDiscomKeys(displayKeys: string[]): string[] {
  return displayKeys.map((displayKey) => {
    const discom = DISCOM.find((d) => d.key === displayKey);
    return discom ? discom.apiKey : displayKey;
  });
}

function getDisplayDiscomName(apiKey: string): string {
  const discom = DISCOM.find((d) => d.apiKey === apiKey);
  return discom ? discom.label : apiKey;
}

interface RajasthanDrawalSummaryAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: any[];
  onGroupByChange: (index: number) => void;
  selectedArea: any[];
  onAreaChange: (v: any) => void;
  selectedDistricts: any[];
  onDistrictsChange: (v: any) => void;
  metrics: DiscomLoadMetrics | null;
}

export function RajasthanDrawalSummaryAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedArea,
  onAreaChange,
  selectedDistricts,
  onDistrictsChange,
  metrics,
}: RajasthanDrawalSummaryAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<string[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [loading, setLoading] = useState(true);

  const [availableAreas, setAvailableAreas] = useState<string[]>([]);
  const [availableDistricts, setAvailableDistricts] = useState<string[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);

  // ─── Fetch ────────────────────────────────────────────────────────────────
  // ▶ DEMO — single summaryData state + conditional fetch (prod fetches all 3 in parallel)
  const fetchSummaryData = useCallback(async () => {
    setLoading(true);
    try {
      const dates = DefaultDates[activeTab]?.defaultRangeDate;
      if (!dates || !dates.startDate || !dates.endDate) {
        setLoading(false);
        return;
      }

      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);
      let data: any[];

      const isAreaGrouping = selectedGroupKeys[0]?.key === "area";
      const isDiscomGrouping = selectedGroupKeys[0]?.key === "discom";
      const isDistrictGrouping = selectedGroupKeys[0]?.key === "district";

      if (isAreaGrouping) {
        data = await getRajasthanGssRestrictedAreaSummaryMetrics(
          startDateStr,
          endDateStr,
        );
      } else if (isDistrictGrouping) {
        data = await getRajasthanGssLoadMetricsRestrictedDiscomDistrict(
          startDateStr,
          endDateStr,
        );
      } else if (isDiscomGrouping) {
        data = await getRajasthanGssRestrictedSummaryMetricsDiscom(
          startDateStr,
          endDateStr,
        );
      } else {
        data = await getRajasthanGssRestrictedAreaSummaryMetrics(
          startDateStr,
          endDateStr,
        );
      }

      setSummaryData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching summary data:", error);
      setSummaryData([]);
      setAvailableAreas([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, selectedGroupKeys]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  // ─── Process ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (summaryData.length === 0) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    const conversionFactor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;

    let filteredData = summaryData;

    if (selectedDiscom.length > 0) {
      const apiDiscomKeys = getApiDiscomKeys(selectedDiscom);
      filteredData = filteredData.filter((item) =>
        apiDiscomKeys.includes(item.discom),
      );
    }

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((opt) => opt.value).includes(item.slot),
      );
    }

    const districts = Array.from(
      new Set(
        filteredData.map((item: any) => {
          const name =
            item.district || item.divisional_headquarter || item.discom;
          const discomName = getDisplayDiscomName(item.discom);
          return `${name} (${discomName})`;
        }),
      ),
    )
      .filter(
        (name) => name && !name.includes("undefined") && !name.includes("null"),
      )
      .sort();
    setAvailableDistricts(districts);

    if (selectedDistricts.length > 0) {
      const distStrings = selectedDistricts.map((d: any) => d.value);
      filteredData = filteredData.filter((item) => {
        const name =
          item.district || item.divisional_headquarter || item.discom;
        const discomName = getDisplayDiscomName(item.discom);
        return distStrings.includes(`${name} (${discomName})`);
      });
    }

    // ▶ DEMO — uses item.area (prod uses item.station)
    const areas = Array.from(
      new Set(
        filteredData
          .map((item: any) => {
            const name = item.area;
            const discomName = getDisplayDiscomName(item.discom);
            if (!name || name === "UNKNOWN") return "";
            return `${name} (${discomName})`;
          })
          .filter(
            (name) =>
              name && !name.includes("undefined") && !name.includes("null"),
          ),
      ),
    ).sort();
    setAvailableAreas(areas);

    if (selectedArea.length > 0) {
      const areaStrings = selectedArea.map((a: any) => a.value);
      filteredData = filteredData.filter((item) => {
        const name = item.area;
        const discomName = getDisplayDiscomName(item.discom);
        return areaStrings.includes(`${name} (${discomName})`);
      });
    }

    if (filteredData.length === 0) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    if (selectedGroupKeys[0]?.key === "area") {
      const slotMap = new Map<number, any>();
      filteredData.forEach((item) => {
        const slot = item.slot;
        const area = item.area || "UNKNOWN";
        // ▶ FIX — normalize spaces → underscores so key matches legends/tooltip cleanKey
        const cleanKey = `${area}_${item.discom}`.replace(/ /g, "_");

        if (!slotMap.has(slot)) {
          slotMap.set(slot, {
            slot,
            slot_number: slot,
            slot_with_time: item.time_string
              ? `${slot} (${item.time_string})`
              : slot,
          });
        }
        const slotData = slotMap.get(slot);
        slotData[`${cleanKey}_min_load`] = item.min_load * conversionFactor;
        slotData[`${cleanKey}_max_load`] = item.max_load * conversionFactor;
        slotData[`${cleanKey}_avg_load`] = item.avg_load * conversionFactor;
        slotData[`${cleanKey}_mode_load`] = item.mode_load * conversionFactor;
        slotData[`${cleanKey}_date_of_min`] = item.date_of_min_load;
        slotData[`${cleanKey}_date_of_max`] = item.date_of_max_load;
      });

      const processedData = Array.from(slotMap.values()).sort(
        (a, b) => a.slot - b.slot,
      );
      setChartData(processedData);
      setTableData(processedData);

      // ▶ FIX — chartKeys also use cleanKey so legends build matching dataKeys
      const uniqueAreas = Array.from(
        new Set(
          filteredData.map((item) =>
            `${item.area || "UNKNOWN"}_${item.discom}`.replace(/ /g, "_"),
          ),
        ),
      ).sort();
      setChartKeys(uniqueAreas);
    } else if (selectedGroupKeys[0]?.key === "discom") {
      const slotMap = new Map<number, any>();
      filteredData.forEach((item) => {
        const slot = item.slot;
        // ▶ FIX — use cleanKey (spaces → underscores) so date keys match
        //   what legends + tooltip build via `key.replace(/ /g, "_")`
        const discomApi = item.discom; // e.g. "Discom 1"
        const cleanKey = discomApi.replace(/ /g, "_"); // e.g. "Discom_1"

        if (!slotMap.has(slot)) {
          slotMap.set(slot, {
            slot,
            slot_number: slot,
            slot_with_time: item.time_string
              ? `${slot} (${item.time_string})`
              : slot,
          });
        }
        const slotData = slotMap.get(slot);
        slotData[`${cleanKey}_min_load`] = item.min_load * conversionFactor;
        slotData[`${cleanKey}_max_load`] = item.max_load * conversionFactor;
        slotData[`${cleanKey}_avg_load`] = item.avg_load * conversionFactor;
        slotData[`${cleanKey}_mode_load`] = item.mode_load * conversionFactor;
        slotData[`${cleanKey}_date_of_min`] = item.date_of_min_load;
        slotData[`${cleanKey}_date_of_max`] = item.date_of_max_load;
      });

      const processedData = Array.from(slotMap.values()).sort(
        (a, b) => a.slot - b.slot,
      );
      setChartData(processedData);
      setTableData(processedData);

      // ▶ FIX — chartKeys must also use cleanKey so legends build matching dataKeys
      const uniqueDiscoms = Array.from(
        new Set(filteredData.map((item) => item.discom.replace(/ /g, "_"))),
      ).sort();
      setChartKeys(uniqueDiscoms);
    } else if (selectedGroupKeys[0]?.key === "district") {
      const slotMap = new Map<number, any>();
      filteredData.forEach((item) => {
        const slot = item.slot;
        const discomDisplay = getDisplayDiscomName(item.discom);

        let district = item.district || item.divisional_headquarter;
        if (
          !district ||
          district === "undefined" ||
          district === "null" ||
          String(district).trim() === ""
        ) {
          district = discomDisplay;
        }
        const districtKey = `${district}_${discomDisplay}`.replace(/ /g, "_");

        if (!slotMap.has(slot)) {
          slotMap.set(slot, {
            slot,
            slot_number: slot,
            slot_with_time: item.time_string
              ? `${slot} (${item.time_string})`
              : slot,
          });
        }
        const slotData = slotMap.get(slot);
        slotData[`${districtKey}_min_load`] = item.min_load * conversionFactor;
        slotData[`${districtKey}_max_load`] = item.max_load * conversionFactor;
        slotData[`${districtKey}_avg_load`] = item.avg_load * conversionFactor;
        slotData[`${districtKey}_mode_load`] =
          item.mode_load * conversionFactor;
        slotData[`${districtKey}_date_of_min`] = item.date_of_min_load;
        slotData[`${districtKey}_date_of_max`] = item.date_of_max_load;
      });

      const processedData = Array.from(slotMap.values()).sort(
        (a, b) => a.slot - b.slot,
      );
      setChartData(processedData);
      setTableData(processedData);

      const uniqueDistricts = Array.from(
        new Set(
          filteredData.map((item) => {
            const discomDisplay = getDisplayDiscomName(item.discom);
            let district = item.district || item.divisional_headquarter;
            if (
              !district ||
              district === "undefined" ||
              district === "null" ||
              String(district).trim() === ""
            ) {
              district = discomDisplay;
            }
            return `${district}_${discomDisplay}`.replace(/ /g, "_");
          }),
        ),
      ).sort();
      setChartKeys(uniqueDistricts);
    }
  }, [
    summaryData,
    selectedUnit,
    selectedDiscom,
    selectedGroupKeys,
    selectedArea,
    selectedDistricts,
    selectedBlock,
  ]);

  // ─── Legends ──────────────────────────────────────────────────────────────
  const legends = useMemo(() => {
    const metricTypes = [
      { field: "min", displayName: "Min", hasDate: true },
      { field: "max", displayName: "Max", hasDate: true },
      { field: "avg", displayName: "Average", hasDate: false },
      { field: "mode", displayName: "Mode", hasDate: false },
    ];

    const validKeys = (chartKeys || []).filter(
      (key) => key && typeof key === "string" && key.trim() !== "",
    );
    if (!validKeys.length) return [];

    return validKeys.flatMap((key) => {
      let baseName = key;
      try {
        if (selectedGroupKeys[0]?.key === "district") {
          // key e.g. "District_1_Discom_1"
          // last 2 parts = discom ("Discom 1"), rest = district name
          const parts = key.split("_");
          const discomKey = parts.slice(-2).join(" "); // "Discom 1"
          const districtKey = parts.slice(0, -2).join(" "); // "District 1"
          baseName = `${districtKey} (${discomKey})`;
        } else if (selectedGroupKeys[0]?.key === "discom") {
          // key is already clean-masked e.g. "Discom_1" → display as "Discom 1"
          baseName = normalizeDisplayLabel(key);
        } else if (selectedGroupKeys[0]?.key === "area") {
          // key is already clean e.g. "Area_1_Discom_1"
          // last segment is discom, rest is area — restore spaces for display
          if (key.includes("_")) {
            // key e.g. "Area_1_Discom_1"
            // DISCOM keys are always "Discom_N" (2 segments), so last 2 parts = discom
            const parts = key.split("_");
            const discomKey = parts.slice(-2).join(" "); // "Discom 1"
            const areaKey = parts.slice(0, -2).join(" "); // "Area 1"
            baseName = `${areaKey} (${discomKey})`;
          }
        }
      } catch (error) {
        baseName = key;
      }

      const cleanKey = key.replace(/ /g, "_");
      return metricTypes.map((metric) => ({
        dataKey: `${key}_${metric.field}_load`,
        name: `${baseName} ${metric.displayName}`,
        legendColor: undefined,
        stroke: undefined,
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "summary" : undefined,
        dateKey: metric.hasDate
          ? `${cleanKey}_date_of_${metric.field}`
          : undefined,
      }));
    });
  }, [chartKeys, selectedGroupKeys, selectedChartType]);

  // ─── Tooltip ──────────────────────────────────────────────────────────────
  const customTooltipFormatter = useCallback(
    (params: any) => {
      const param = Array.isArray(params) ? params[0] : params;
      let items = Array.isArray(params) ? params : [params];

      let title = param.name;
      title = getTimeRange(parseInt(param.name)) + ` (${param.name})`;
      const dataPoint = chartData[param.dataIndex];

      const legendOrder = legends.map((l: any) => l.name);
      const sortedItems = [...items].sort((a, b) => {
        const ai = legendOrder.indexOf(a.seriesName);
        const bi = legendOrder.indexOf(b.seriesName);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      });

      const tooltipItems = sortedItems.map((item) => {
        const value = parseFloat(item.value)
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

        let displaySeriesName = item.seriesName;

        // ▶ FIX — look up the matching legend by seriesName to get the exact
        //   dataKey, then derive the cleanKey prefix from it instead of trying
        //   to reconstruct from seriesName (which has spaces and brackets).
        const matchingLegend = legends.find(
          (leg) => leg.name === item.seriesName,
        );
        const dataKey = matchingLegend?.dataKey || "";
        const isMin = dataKey.endsWith("_min_load");
        const isMax = dataKey.endsWith("_max_load");
        const keyPrefix = dataKey
          .replace(/_min_load$/, "")
          .replace(/_max_load$/, "")
          .replace(/_avg_load$/, "")
          .replace(/_mode_load$/, "");

        if (isMin && dataPoint?.[`${keyPrefix}_date_of_min`]) {
          displaySeriesName = `${item.seriesName} (${dataPoint[`${keyPrefix}_date_of_min`]})`;
        } else if (isMax && dataPoint?.[`${keyPrefix}_date_of_max`]) {
          displaySeriesName = `${item.seriesName} (${dataPoint[`${keyPrefix}_date_of_max`]})`;
        }

        return `
        <div class="tooltip-item" data-name="${item.seriesName?.toLowerCase()}">
          <div class="color-indicator" style="background-color: ${item.color}"></div>
          <div class="series-name">${displaySeriesName}</div>
          <div class="value">${value} ${selectedUnit}</div>
        </div>`;
      });

      return `
      <div class="enhanced-tooltip">
        <div class="tooltip-header"><div class="tooltip-title">${title}</div></div>
        <div class="tooltip-content">${tooltipItems.join("")}</div>
      </div>
      <style>
        .enhanced-tooltip{font-family:'Inter',system-ui,sans-serif;width:100%;min-width:200px}
        .tooltip-header{display:flex;flex-direction:column;gap:8px;justify-content:start;align-items:start;border-bottom:1px solid #eaeaea;padding-bottom:8px;margin-bottom:8px}
        .tooltip-title{font-weight:600;font-size:13px;color:#333}
        .tooltip-content{max-height:300px;overflow-y:auto;padding-right:10px}
        .tooltip-item{display:flex;align-items:center;gap:8px;padding:4px 0;border-bottom:1px solid #f0f0f0}
        .tooltip-item:last-child{border-bottom:none}
        .color-indicator{width:10px;height:10px;flex-shrink:0;border-radius:50%}
        .series-name{font-weight:500;font-size:12px;color:#333;flex:1}
        .value{font-weight:600;font-size:12px;color:#333}
      </style>`;
    },
    [chartData, selectedUnit, legends],
  );

  // ─── Table helpers ────────────────────────────────────────────────────────
  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends, [chartData]);

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData,
    defaultSortKey: "block",
    itemsPerPage: 96,
    unit: selectedUnit,
    block_key: "slot_number",
    extraKeys: ["slot_number", "Date"],
    extraLabels: ["Block", "Date"],
    filename: AddDatesWithFileName({
      fileName: "Restricted Demand Summary",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Summary"]}
            onSelect={onXAxisModeChange}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => onUnitChange(item)}
          />
          <FilterMenu
            title="Discom"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={() => {}}
            setSelectedItems={(item) => onDiscomChange(item)}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.label)}
            selectedItems={selectedGroupKeys.map((item) => item.label)}
            onSelect={(item, index) => {
              onGroupByChange(index);
              onAreaChange([]);
            }}
          />
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(H_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChip
              icon={<PeakIcon width={20} height={19} />}
              items={[
                {
                  label: "Peak Load",
                  value: metrics["Peak Load (MW)"].value,
                  unit: metrics["Peak Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<AvgIcon width={20} height={19} />}
              items={[
                {
                  label: "Average Load",
                  value: metrics["Average Load (MW)"].value,
                  unit: metrics["Average Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<MinIcon width={20} height={19} />}
              items={[
                {
                  label: "Minimum Load",
                  value: metrics["Minimum Load (MW)"].value,
                  unit: metrics["Minimum Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<TotalIcon width={20} height={19} />}
              items={[
                {
                  label: "Total Energy",
                  value: metrics["Total Energy (MU)"].value,
                  unit: metrics["Total Energy (MU)"].unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Restricted Demand Summary"
              subtitle="Fetching Demo State restricted demand summary data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-80">
                  Restricted Demand Summary ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 justify-end">
                  {selectedGroupKeys[0]?.key === "district" && (
                    <div className="min-w-60">
                      <SearchBox
                        options={availableDistricts.map((item) => ({
                          value: item,
                          label: item,
                        }))}
                        isMany={true}
                        placeholder="Search District"
                        selectedOptions={selectedDistricts}
                        onChange={(v: any) => {
                          onDistrictsChange(v);
                          onAreaChange([]);
                        }}
                      />
                    </div>
                  )}
                  {(selectedGroupKeys[0]?.key === "area" ||
                    (selectedGroupKeys[0]?.key === "district" &&
                      selectedDistricts.length > 0)) && (
                    <div className="min-w-60">
                      <SearchBox
                        options={availableAreas.map((item) => ({
                          value: item,
                          label: item,
                        }))}
                        isMany={true}
                        placeholder="Search Area"
                        selectedOptions={selectedArea}
                        onChange={(v: any) => onAreaChange(v)}
                      />
                    </div>
                  )}
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      dataTab.label === "Chart" &&
                        downloadChartTableCSV(tablePar);
                      dataTab.label === "Summary" &&
                        downloadSummaryCSV(tablePar);
                      dataTab.label === "Table" &&
                        downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {chartData.length === 0 && (
                <div
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#666",
                  }}
                >
                  No data available for the selected filters. Please adjust your
                  selection.
                </div>
              )}

              {dataTab.label === "Chart" && chartData.length > 0 && (
                <ReMixChart
                  key={`${selectedChartType.value}-${selectedUnit}`}
                  data={chartData}
                  showBrush={undefined}
                  isTimeSlot={false}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  legends={legends.map((leg) => ({
                    ...leg,
                    type: selectedChartType.value,
                    stackId: selectedChartType.stackId ?? undefined,
                  }))}
                  xDataKey="slot"
                  secondXDataKey={undefined}
                  customTooltip={customTooltipFormatter}
                />
              )}
              {dataTab.label === "Summary" && chartData.length > 0 && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && chartData.length > 0 && (
                <ChartTable {...tablePar} />
              )}
            </>
          )}
        </div>

        {/* ▶ DEMO — simplified footer (no Source tooltip, "Demo Version" label) */}
        <div className="gna-footer-container">
          <div className="grid grid-cols-2 justify-between gap-5">
            <p className="gna-footer-text text-start col-span-1">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Demo Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
